'use client'

import ChevronLeftIcon from '@heroicons/react/20/solid/ChevronLeftIcon'
import ChevronRightIcon from '@heroicons/react/20/solid/ChevronRightIcon'
import type { KeenSliderInstance } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { getPathLocale } from '@src/i18n/locale.util'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

import { Pill } from './Pill'

export interface PillsNavProps {
  items: Array<{ label: any; href: string; isVisible: boolean }>
  builderBlock?: any
}

export function PillsNav({ items = [] }: PillsNavProps) {
  const [isControlVisible, setIsControlVisible] = useState({ prev: false, next: false })
  const trackSelectNavCta = useTrackSelectNavCta()
  const pathname = usePathname()

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    mode: 'free',
    rubberband: false,
    slides: { perView: 'auto', spacing: 8 },
    created: syncSlider,
    updated: syncSlider,
    detailsChanged: syncSlider,
  })

  function syncSlider(slider: KeenSliderInstance) {
    const { position, min, max, length } = slider.track.details
    const isOverflowing = length > 0
    setIsControlVisible({
      prev: isOverflowing && position > min,
      next: isOverflowing && position < max,
    })
  }

  return (
    <div className="relative -mx-4 lg:mx-0">
      <nav
        ref={ref}
        className={cn('keen-slider flex', {
          'lg:mask-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,1)_50%,rgba(0,0,0,1)_90%,rgba(255,255,255,0)_100%)]':
            isControlVisible.prev && isControlVisible.next,
          'lg:mask-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,1)_50%)]':
            isControlVisible.prev && !isControlVisible.next,
          'lg:mask-[linear-gradient(90deg,rgba(0,0,0,1)_90%,rgba(255,255,255,0)_100%)]':
            !isControlVisible.prev && isControlVisible.next,
        })}
      >
        {items
          .filter((i) => !!i.isVisible)
          .map((item, index) => {
            return (
              <div
                key={index}
                className="keen-slider__slide w-auto! shrink-0 first-of-type:max-lg:pl-4 last-of-type:max-lg:pr-4"
              >
                <LocalizedLink
                  href={item.href}
                  onClick={() => trackSelectNavCta({ 'cta url': item.href })}
                >
                  <Pill
                    key={index}
                    label={item.label}
                    isActive={pathname === `${getPathLocale(pathname)}${item.href}`}
                  />
                </LocalizedLink>
              </div>
            )
          })}
      </nav>

      <button
        onClick={() => {
          if (!slider.current) return
          const { moveToIdx, track } = slider.current
          const currentIndex = track.details.slides.findIndex((slide) => slide.portion >= 1) ?? 0
          const prevIndex = Math.max(track.details.minIdx, currentIndex - 4)
          moveToIdx(prevIndex)
        }}
        className={cn('absolute top-0 -left-8 hidden h-full pr-10', {
          'lg:block': isControlVisible.prev,
        })}
      >
        <ChevronLeftIcon className="size-7" />
      </button>
      <button
        onClick={() => {
          if (!slider.current) return
          const { moveToIdx, track } = slider.current
          const currentIndex = track.details.slides.findIndex((slide) => slide.portion >= 1) ?? 0
          const nextIdx = Math.min(track.details.maxIdx, currentIndex + 3)
          moveToIdx(nextIdx)
        }}
        className={cn('absolute top-0 -right-8 hidden h-full pl-10', {
          'lg:block': isControlVisible.next,
        })}
      >
        <ChevronRightIcon className="size-7" />
      </button>
    </div>
  )
}
